import type { JobType } from '../../../../reference-data/reference-data.api';
import type { JobDetail } from '../../../jobs.api';

/** The fixed set of items intake tracks — feeds the later dispatch readiness checklist, not that checklist itself (PRD → Job intake). */
export const JOB_INTAKE_FIELDS = [
  'originZoneCode',
  'destinationZoneCode',
  'requestedDate',
  'customerDeliveryDeadline',
  'source',
  'contact',
  'requestedQuantity',
  'attachment',
  'commodity',
  'weight',
  'dropPoint',
] as const;
export type JobIntakeField = (typeof JOB_INTAKE_FIELDS)[number];

export const JOB_INTAKE_FIELD_LABELS: Record<JobIntakeField, string> = {
  originZoneCode: 'Origin zone',
  destinationZoneCode: 'Destination zone',
  requestedDate: 'Requested date',
  customerDeliveryDeadline: 'Customer delivery deadline',
  source: 'Source',
  contact: 'Who asked',
  requestedQuantity: 'Quantity',
  attachment: 'Original message / attachment',
  commodity: 'Commodity',
  weight: 'Weight',
  dropPoint: 'Drop points',
};

function isLooseFamily(job: JobDetail, jobTypes: JobType[]): boolean {
  return jobTypes.find((t) => t.code === job.jobTypeCode)?.family === 'loose';
}

/** Loose-cargo-only fields: commodity, weight and at least one drop point (PRD → Job intake → Captured per job type (Loose cargo); gitaumoses4/chukua#32). Informational only — a loose job can confirm with none of them set. */
function missingLooseCargoFields(job: JobDetail): JobIntakeField[] {
  const missing: JobIntakeField[] = [];
  if (!job.looseCargoDetails) missing.push('commodity');
  if (!job.looseCargoDetails?.weightKg) missing.push('weight');
  if (job.dropPoints.length === 0) missing.push('dropPoint');
  return missing;
}

function missingBaseFields(job: JobDetail): JobIntakeField[] {
  const missing: JobIntakeField[] = [];
  if (!job.originZoneCode) missing.push('originZoneCode');
  if (!job.destinationZoneCode) missing.push('destinationZoneCode');
  if (!job.requestedDate) missing.push('requestedDate');
  if (!job.customerDeliveryDeadline) missing.push('customerDeliveryDeadline');
  if (!job.source) missing.push('source');
  if (!job.contactName && !job.contactPhone) missing.push('contact');
  if (!job.requestedQuantity) missing.push('requestedQuantity');
  if (job.attachments.length === 0) missing.push('attachment');
  return missing;
}

/** Which of the fixed intake fields a quick-created job still has blank — a clerk finishing what intake left open, not a blocking validation. */
export function missingIntakeFields(job: JobDetail, jobTypes: JobType[] = []): JobIntakeField[] {
  const missing = missingBaseFields(job);
  if (isLooseFamily(job, jobTypes)) missing.push(...missingLooseCargoFields(job));
  return missing;
}
